import { FontWeight, FontSize } from '../types'

export interface GoogleFont {
  family: string
  category: 'sans-serif' | 'serif' | 'display' | 'monospace'
  weights: FontWeight[]
  googleName: string // URL-encoded name for Google Fonts API
}

// 20 curated Google Fonts
export const GOOGLE_FONTS: GoogleFont[] = [
  // Sans-serif (UI/Body)
  {
    family: 'System UI',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: '', // No loading needed
  },
  {
    family: 'Inter',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Inter',
  },
  {
    family: 'Roboto',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'bold', 'black'],
    googleName: 'Roboto',
  },
  {
    family: 'Open Sans',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Open+Sans',
  },
  {
    family: 'Lato',
    category: 'sans-serif',
    weights: ['normal', 'bold', 'black'],
    googleName: 'Lato',
  },
  {
    family: 'Poppins',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Poppins',
  },
  {
    family: 'Nunito',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Nunito',
  },
  {
    family: 'Work Sans',
    category: 'sans-serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Work+Sans',
  },
  // Display (Headings)
  {
    family: 'Montserrat',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Montserrat',
  },
  {
    family: 'Playfair Display',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold', 'black'],
    googleName: 'Playfair+Display',
  },
  {
    family: 'DM Sans',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'DM+Sans',
  },
  {
    family: 'Space Grotesk',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Space+Grotesk',
  },
  {
    family: 'Sora',
    category: 'display',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Sora',
  },
  // Serif (Elegant)
  {
    family: 'Merriweather',
    category: 'serif',
    weights: ['normal', 'bold', 'black'],
    googleName: 'Merriweather',
  },
  {
    family: 'Lora',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Lora',
  },
  {
    family: 'Crimson Pro',
    category: 'serif',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'Crimson+Pro',
  },
  {
    family: 'Source Serif Pro',
    category: 'serif',
    weights: ['normal', 'semibold', 'bold', 'black'],
    googleName: 'Source+Serif+Pro',
  },
  // Monospace
  {
    family: 'Source Code Pro',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Source+Code+Pro',
  },
  {
    family: 'JetBrains Mono',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold', 'extrabold'],
    googleName: 'JetBrains+Mono',
  },
  {
    family: 'Fira Code',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'Fira+Code',
  },
  {
    family: 'IBM Plex Mono',
    category: 'monospace',
    weights: ['normal', 'medium', 'semibold', 'bold'],
    googleName: 'IBM+Plex+Mono',
  },
]

// Get font by family name
export function getFontByFamily(family: string): GoogleFont | undefined {
  return GOOGLE_FONTS.find((f) => f.family === family)
}

// Get fonts by category
export function getFontsByCategory(category: GoogleFont['category']): GoogleFont[] {
  return GOOGLE_FONTS.filter((f) => f.category === category)
}

// Get display/body fonts (sans-serif, serif, display)
export function getDisplayFonts(): GoogleFont[] {
  return GOOGLE_FONTS.filter((f) => f.category !== 'monospace')
}

// Get body fonts (sans-serif primarily)
export function getBodyFonts(): GoogleFont[] {
  return GOOGLE_FONTS.filter((f) => f.category === 'sans-serif' || f.category === 'serif')
}

// Get monospace fonts
export function getMonoFonts(): GoogleFont[] {
  return GOOGLE_FONTS.filter((f) => f.category === 'monospace')
}

// Font weight to numeric value mapping
export const FONT_WEIGHT_VALUES: Record<FontWeight, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
}

// Font weight display labels
export const FONT_WEIGHT_LABELS: Record<FontWeight, string> = {
  normal: 'Normal',
  medium: 'Medium',
  semibold: 'Semibold',
  bold: 'Bold',
  extrabold: 'Extra Bold',
  black: 'Black',
}

// Check if a weight is available for a font
export function isWeightAvailable(family: string, weight: FontWeight): boolean {
  const font = getFontByFamily(family)
  if (!font) return false
  return font.weights.includes(weight)
}

// Get CSS font-family value
export function getFontFamilyValue(family: string): string {
  if (family === 'System UI') {
    return "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  }
  const font = getFontByFamily(family)
  if (!font) return family

  const fallback = font.category === 'monospace'
    ? 'ui-monospace, monospace'
    : font.category === 'serif'
    ? 'Georgia, serif'
    : 'system-ui, sans-serif'

  return `'${family}', ${fallback}`
}

// Build Google Fonts URL for multiple fonts
export function buildGoogleFontsUrl(families: string[]): string | null {
  const fontsToLoad = families
    .map((family) => getFontByFamily(family))
    .filter((f): f is GoogleFont => f !== undefined && f.googleName !== '')

  if (fontsToLoad.length === 0) return null

  const familyParams = fontsToLoad.map((font) => {
    const weights = font.weights.map((w) => FONT_WEIGHT_VALUES[w]).join(';')
    return `family=${font.googleName}:wght@${weights}`
  })

  return `https://fonts.googleapis.com/css2?${familyParams.join('&')}&display=swap`
}

// Font size values (Tailwind CSS scale)
export const FONT_SIZE_VALUES: Record<FontSize, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
  '8xl': '6rem',
}

// Font size line heights
export const FONT_SIZE_LINE_HEIGHTS: Record<FontSize, string> = {
  xs: '1rem',
  sm: '1.25rem',
  base: '1.5rem',
  lg: '1.75rem',
  xl: '1.75rem',
  '2xl': '2rem',
  '3xl': '2.25rem',
  '4xl': '2.5rem',
  '5xl': '1',
  '6xl': '1',
  '7xl': '1',
  '8xl': '1',
}

// Font size display labels
export const FONT_SIZE_LABELS: Record<FontSize, string> = {
  xs: 'XS',
  sm: 'Small',
  base: 'Base',
  lg: 'Large',
  xl: 'XL',
  '2xl': '2XL',
  '3xl': '3XL',
  '4xl': '4XL',
  '5xl': '5XL',
  '6xl': '6XL',
  '7xl': '7XL',
  '8xl': '8XL',
}

// All available font sizes
export const FONT_SIZES: FontSize[] = ['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl']

// Display font sizes (larger sizes for headings)
export const DISPLAY_FONT_SIZES: FontSize[] = ['lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl', '8xl']

// Body font sizes (smaller sizes for text)
export const BODY_FONT_SIZES: FontSize[] = ['xs', 'sm', 'base', 'lg']
